import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '../ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  Menu,
  User,
  LogOut,
  LayoutDashboard,
  Settings,
  ChevronDown,
  Facebook,
  Instagram,
  Twitter,
  Mail,
  Phone,
  Award,
  Star,
  Home,
  Handshake,
  Globe,
  Bot,
  CreditCard,
  TrendingUp,
  Bell,
  Building2
} from 'lucide-react';

const navLinks = [
  { name: 'Inicio', path: '/' },
  { name: '¿Quiénes Somos?', path: '/nosotros' },
  { name: 'Beneficios', path: '/beneficios' },
  { name: 'Proveedores', path: '/proveedores' },
  { name: 'Calendario', path: '/calendario' },
  { 
    name: 'Formación', 
    path: '/academia',
    submenu: [
      { name: 'Academia UPIM', path: '/academia' },
      { name: 'Webinars', path: '/webinars' },
      { name: 'Certificaciones SEP', path: '/certificaciones' },
    ]
  },
  {
    name: 'UPIM IA',
    path: '/upim-ia',
    submenu: [
      { name: 'Asesorías con IA', path: '/asesorias' },
      { name: 'Chat UPIM IA', path: '/upim-ia/chat' },
      { name: 'Directorio Liderazgo', path: '/directorio' },
    ]
  },
  { name: 'Recursos', path: '/recursos' },
  { name: 'Contacto', path: '/contacto' },
];

// CRM submenu items for authenticated users
const crmMenuItems = [
  { name: 'Dashboard CRM', path: '/crm', icon: LayoutDashboard },
  { name: 'Mis Propiedades', path: '/crm/propiedades', icon: Home },
  { name: 'Alianzas', path: '/crm/alianzas', icon: Handshake },
  { name: 'Mi Micrositio', path: '/crm/micrositio', icon: Globe },
  { name: 'Bot IA Clientes', path: '/crm/bot-clientes', icon: Bot },
  { name: 'Credencial Digital', path: '/crm/credencial-digital', icon: CreditCard },
  { name: 'Reportes de Mercado', path: '/crm/reportes-mercado', icon: TrendingUp },
  { name: 'Alertas', path: '/crm/alertas', icon: Bell },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin':
      case 'superadmin':
        return '/admin';
      case 'provider':
        return '/proveedor';
      default:
        return '/dashboard';
    }
  };

  return (
    <>
      {/* Top Bar - Redes Sociales y Contacto */}
      <div className="hidden lg:block bg-gray-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <a href="mailto:info@upim.org" className="flex items-center space-x-1 text-gray-300 hover:text-white transition-colors">
                <Mail className="h-3.5 w-3.5" />
                <span>info@upim.org</span>
              </a>
              <a href="tel:5571164745" className="flex items-center space-x-1 text-gray-300 hover:text-white transition-colors">
                <Phone className="h-3.5 w-3.5" />
                <span>55 7116 4745</span>
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <a
                href="https://www.instagram.com/upimnacional/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://twitter.com/upimnacional"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/upimnacional"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - más grande */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="/assets/upim-logo.svg"
                alt="UPIM Logo"
                className="h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation - Menú expandido horizontal */}
            <div className="hidden lg:flex items-center flex-1 justify-center space-x-1">
              {navLinks.map((link) => (
                link.submenu ? (
                  <DropdownMenu key={link.path}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1 ${
                          location.pathname === link.path || location.pathname === '/certificaciones'
                            ? 'text-gray-900 bg-gray-100'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-48">
                      {link.submenu.map((sublink) => (
                        <DropdownMenuItem 
                          key={sublink.path}
                          onClick={() => navigate(sublink.path)}
                          className={location.pathname === sublink.path ? 'bg-gray-100' : ''}
                        >
                          {sublink.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'text-gray-900 bg-gray-100'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              {isAuthenticated ? (
                <>
                  {/* CRM Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex items-center gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                        <Building2 className="h-4 w-4" />
                        <span className="text-sm font-medium">CRM</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {crmMenuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem 
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={location.pathname === item.path ? 'bg-indigo-50' : ''}
                          >
                            <Icon className="mr-2 h-4 w-4 text-indigo-600" />
                            {item.name}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center space-x-2">
                        <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{user?.name?.split(' ')[0]}</span>
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate(getDashboardPath())}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/mis-constancias')}>
                        <Award className="mr-2 h-4 w-4" />
                        Mis Constancias
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/mis-insignias')}>
                        <Star className="mr-2 h-4 w-4" />
                        Compartir Insignias
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/perfil')}>
                        <Settings className="mr-2 h-4 w-4" />
                        Mi Perfil
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                        <LogOut className="mr-2 h-4 w-4" />
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    onClick={() => navigate('/afiliacion')}
                    className="bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Afíliate
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0">
                <VisuallyHidden><SheetTitle>Menú de navegación</SheetTitle></VisuallyHidden>
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b">
                    <div className="flex items-center">
                      <img
                        src="/assets/upim-logo.svg"
                        alt="UPIM Logo"
                        className="h-12 w-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto py-4">
                    <div className="space-y-1 px-3">
                      {navLinks.map((link) => (
                        link.submenu ? (
                          <div key={link.path} className="space-y-1">
                            <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                              {link.name}
                            </p>
                            {link.submenu.map((sublink) => (
                              <Link
                                key={sublink.path}
                                to={sublink.path}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ml-2 ${
                                  location.pathname === sublink.path
                                    ? 'text-gray-900 bg-gray-100'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                {sublink.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                              location.pathname === link.path
                                ? 'text-gray-900 bg-gray-100'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                          >
                            {link.name}
                          </Link>
                        )
                      ))}
                    </div>
                    
                    {/* Redes sociales en móvil */}
                    <div className="px-6 pt-6 border-t mt-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Síguenos</p>
                      <div className="flex space-x-4">
                        <a href="https://www.instagram.com/upimnacional/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-900">
                          <Instagram className="h-5 w-5" />
                        </a>
                        <a href="https://twitter.com/upimnacional" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-900">
                          <Twitter className="h-5 w-5" />
                        </a>
                        <a href="https://www.facebook.com/upimnacional" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-900">
                          <Facebook className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border-t space-y-3">
                    {isAuthenticated ? (
                      <>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() => {
                            navigate(getDashboardPath());
                            setIsOpen(false);
                          }}
                        >
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Dashboard
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Cerrar Sesión
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            navigate('/login');
                            setIsOpen(false);
                          }}
                        >
                          Iniciar Sesión
                        </Button>
                        <Button
                          className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                          onClick={() => {
                            navigate('/afiliacion');
                            setIsOpen(false);
                          }}
                        >
                          Afíliate
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
